import React, { useMemo, useState } from "react";
import { connect } from "react-redux";
import "./EmeraldSalesContactForm.scss";
import {
  EmeraldBigGreenIcon,
  DarkEmeraldBigGreenIcon,
  EmeraldBigTextIcon,
  IncomeAlertsTextIcon,
} from "../../common/Icon";
import { Button } from "@material-ui/core";
import { useAuth } from "../../../contexts/Auth";
import PhoneNumberInput from "./PhoneNumberInput";
import { contactFormLoad } from "../../../appRedux/ducks/contactForm";
import { addUserSettingsLoad } from "../../../appRedux/ducks/userSettings";
import moment from "moment";
import WealthSeriesSignupLogo from "../../../assets/images/wealth-series-signup/product-logo.png";
import CloseIcon from "@mui/icons-material/Close";

const WEALTH_SERIES_TIER_TYPES = [
  "WEALTH SERIES CORE",
  "WEALTH SERIES ATLAS",
  "WEALTH SERIES TITAN",
  "WEALTH SERIES EVEREST",
  "WEALTH SERIES PULSAR",
  "WEALTH SERIES RANGER",
  "WEALTH SERIES FALCON",
  "WEALTH SERIES QUANTUM",
];

const ContactForm = ({
  themeMode,
  onClose,
  submitContactForm,
  userSettings,
  setUserSettings,
  product_type,
  // product types: 'PLATINUM STANDARD', OPTIONS MENTORSHIP INTERMEDIATE, OPTIONS MENTORSHIP ADVANCED, OPTIONS MENTORSHIP STARTER 'OPTIONS MENTORSHIP PLATINUM', "EMERAL INCOME ALERTS",
  // "WEALTH SERIES" and one per tier: "WEALTH SERIES CORE|ATLAS|TITAN|EVEREST|PULSAR|RANGER|FALCON|QUANTUM" (TRAD-1317)
}) => {
  const [name, setName] = useState("");
  const [phone, setPhone] = useState("");
  const [errors, setErrors] = useState({});
  const [isSubmitted, setIsSubmitted] = useState(false);
  const { userData, user } = useAuth();
  const email = userData?.email || user?.email;
  // Blackbox (TRAD-1293): the dashboard prototype styles this form as
  // "Sales Inquiry — Blackbox" with a corner close control, light in both
  // themes (see .blackbox-contact-form).
  const isBlackbox = product_type === "BLACKBOX";
  // A Wealth Series tier picked on the paywall (TRAD-1317): same form and
  // hold as the series-wide request, the tier only travels in the lead.
  const isWealthSeriesTier = WEALTH_SERIES_TIER_TYPES.includes(product_type);

  const handlePhoneChange = (value) => {
    setPhone(value);
    if (errors.phone) {
      setErrors((prevErrors) => ({ ...prevErrors, phone: undefined }));
    }
  };

  const color = useMemo(() => {
    return themeMode === "dark" ? "#fff" : "#000";
  }, [themeMode]);

  const validateForm = () => {
    let currentErrors = {};
    let formIsValid = true;
    const phoneRegex =
      /^[+]?[0-9]{0,3}\W?[(]?[0-9]{3}[)]?[-\s.]?[0-9]{3}[-\s.]?[0-9]{4,6}$/im;

    if (!name.trim()) {
      currentErrors.name = "Name is required.";
      formIsValid = false;
    }

    if (!phone.trim()) {
      currentErrors.phone = "Phone number is required.";
      formIsValid = false;
    } else if (!phoneRegex.test(phone)) {
      currentErrors.phone = "Please enter a valid phone number.";
      formIsValid = false;
    }

    return { isValid: formIsValid, errors: currentErrors };
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    const { isValid, errors: validationErrors } = validateForm();
    if (isValid) {
      submitContactForm({ email, name, phone, product_type });
      let settings = { ...userSettings };
      const currentDate = moment().format("YYYY-MM-DD");
      switch (product_type) {
        case "PLATINUM STANDARD":
          settings.platinumStandardContact = currentDate;
          break;
        case "OPTIONS MENTORSHIP PLATINUM":
          settings.omPlatinumContact = currentDate;
          break;
        case "EMERALD INCOME ALERTS":
          settings.emeraldContact = currentDate;
          break;
        case "OPTIONS MENTORSHIP INTERMEDIATE":
          settings.omIntermediateContact = currentDate;
          break;
        case "OPTIONS MENTORSHIP ADVANCED":
          settings.omAdvancedContact = currentDate;
          break;
        case "OPTIONS MENTORSHIP STARTER":
          settings.omStarterContact = currentDate;
          break;
        case "WEALTH SERIES":
        case "WEALTH SERIES CORE":
        case "WEALTH SERIES ATLAS":
        case "WEALTH SERIES TITAN":
        case "WEALTH SERIES EVEREST":
        case "WEALTH SERIES PULSAR":
        case "WEALTH SERIES RANGER":
        case "WEALTH SERIES FALCON":
        case "WEALTH SERIES QUANTUM":
          settings.wsContact = currentDate;
          break;
        case "WEALTH SERIES LITE":
          settings.wsLiteContact = currentDate;
          break;
        case "CRYPTO OBSIDIAN":
          settings.cryptoObsidianContact = currentDate;
          break;
        case "WEALTH SERIES BITCOIN NAVIGATOR":
          settings.wealthSeriesBitcoinNavigatorContact = currentDate;
          break;
        case "BLACKBOX":
          settings.blackboxContact = currentDate;
          break;
        case "AI OPTIONS ALERTS":
          settings.aiAlertsContact = currentDate;
          break;
        default:
          console.error(`Unknown product type: ${product_type}`);
      }
      setUserSettings(settings, false, true);
      setIsSubmitted(true);
      setErrors({});
    } else {
      setErrors(validationErrors);
    }
  };

  const renderLogos = () => {
    switch (product_type) {
      case "PLATINUM STANDARD":
        return (
          <div className="emerald-sales-contact-form__header gap-0">
            <div className="platinum-program-sign-up-title in-modal">
              The Platinum Program
            </div>
          </div>
        );
      case "OPTIONS MENTORSHIP PLATINUM":
        return (
          <div className="emerald-sales-contact-form__header gap-0">
            <div className="platinum-program-sign-up-title in-modal">
              Options Mentorship Platinum
            </div>
          </div>
        );
      case "EMERALD INCOME ALERTS":
        return (
          <div className="emerald-sales-contact-form__header">
            <div className="emerald-sales-contact-form__logo">
              {themeMode === "light" ? (
                <EmeraldBigGreenIcon />
              ) : (
                <DarkEmeraldBigGreenIcon />
              )}
            </div>
            <div className="emerald-sales-contact-form__text_logos">
              <EmeraldBigTextIcon fill={color} />
              <IncomeAlertsTextIcon fill={color} />
            </div>
          </div>
        );
      case "WEALTH SERIES":
      case "WEALTH SERIES CORE":
      case "WEALTH SERIES ATLAS":
      case "WEALTH SERIES TITAN":
      case "WEALTH SERIES EVEREST":
      case "WEALTH SERIES PULSAR":
      case "WEALTH SERIES RANGER":
      case "WEALTH SERIES FALCON":
      case "WEALTH SERIES QUANTUM":
        return (
          <img
            src={WealthSeriesSignupLogo}
            alt="Wealth Series Logo"
            className="w-full"
          />
        );
      case "WEALTH SERIES LITE":
        return (
          <img
            src={WealthSeriesSignupLogo}
            alt="Wealth Series Lite Logo"
            className="w-full"
          />
        );
      case "CRYPTO OBSIDIAN":
      case "BLACKBOX":
        return null;

      default:
        return (
          <div className="emerald-sales-contact-form__header">
            {" "}
            {product_type}{" "}
          </div>
        );
    }
  };

  return (
    <div
      className={`emerald-sales-contact-form ${product_type === "WEALTH SERIES" || isWealthSeriesTier ? "ws-core-contact-form" : ""} ${product_type === "CRYPTO OBSIDIAN" ? "crypto-obsidian-contact-form" : ""} ${isBlackbox ? "blackbox-contact-form" : ""}`}
    >
      {isBlackbox && (
        <button
          type="button"
          aria-label="Close"
          className="blackbox-contact-form__close"
          onClick={onClose}
        >
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
            <path
              d="M19 6.41L17.59 5L12 10.59L6.41 5L5 6.41L10.59 12L5 17.59L6.41 19L12 13.41L17.59 19L19 17.59L13.41 12L19 6.41Z"
              fill="currentColor"
            />
          </svg>
        </button>
      )}
      {renderLogos()}
      {isSubmitted ? (
        <div className="emerald-sales-contact-form__info">
          <div className="emerald-sales-contact-form__title">
            Thank you for expressing interest
          </div>
          <div className="emerald-sales-contact-form__subtitle">
            You are one step closer towards elite trading success. Our sales
            executive will get in touch with you shortly.{" "}
          </div>
        </div>
      ) : (
        <div className="emerald-sales-contact-form__info">
          <div className="emerald-sales-contact-form__title">
            {product_type === "CRYPTO OBSIDIAN" ? (
              <div className="contact-sales-crypto-title">
                <span>Sales Inquiry on Crypto Obsidian</span>
                <CloseIcon onClick={onClose} />
              </div>
            ) : isBlackbox ? (
              "Sales Inquiry — Blackbox"
            ) : (
              "Sales Contact Form"
            )}
          </div>
          <div className="emerald-sales-contact-form__subtitle">
            {product_type === "CRYPTO OBSIDIAN" ? (
              <div className="crypto-obsidian-subtitle">
                Thank you for expressing interest.
                <br />
                <br />
                Fill in your contact details below and our sales executive will
                get in touch with you shortly.
              </div>
            ) : (
              "Thank you for expressing interest. Fill in your contact details below and our sales executive will get in touch with you shortly."
            )}
          </div>
        </div>
      )}

      {isSubmitted ? (
        <div className="emerald-sales-contact-form__content">
          <Button
            variant="contained"
            className={`emerald-sales-submit-button ${product_type.includes("PLATINUM") ? "platinum-background" : product_type.includes("WEALTH SERIES") ? "ws-background" : ""} ${product_type === "CRYPTO OBSIDIAN" ? "crypto-obsidian-background" : ""} ${isBlackbox ? "blackbox-background" : ""}`}
            onClick={onClose}
          >
            Close
          </Button>
        </div>
      ) : (
        <div className="emerald-sales-contact-form__content">
          <form onSubmit={handleSubmit}>
            <div className="emerald-sales-contact-form__input">
              <label htmlFor="name">Name</label>
              <input
                type="text"
                id="name"
                placeholder="Enter Name"
                value={name}
                onChange={(e) => setName(e.target.value)}
              />
              {errors.name && <div className="error">{errors.name}</div>}
            </div>
            <div className="emerald-sales-contact-form__input ">
              <label htmlFor="email">Email Address</label>
              <input
                className="disabled-cursor"
                type="email"
                id="email"
                placeholder="Enter Email"
                value={email}
                disabled
              />
            </div>
            <div className="emerald-sales-contact-form__input phone-input-emerald">
              <PhoneNumberInput value={phone} onChange={handlePhoneChange} />
              {errors.phone && <div className="error">{errors.phone}</div>}
            </div>
            <Button
              type="submit"
              variant="contained"
              className={`emerald-sales-submit-button ${product_type.includes("PLATINUM") ? "platinum-background" : product_type.includes("WEALTH SERIES") ? "ws-background" : ""} ${product_type === "CRYPTO OBSIDIAN" ? "crypto-obsidian-background" : ""} ${product_type === "AI OPTIONS ALERTS" ? "ai-options-alerts-background" : ""} ${isBlackbox ? "blackbox-background" : ""}`}
            >
              Submit
            </Button>
            <Button
              variant="text"
              className={`emerald-sales-cancel-button ${product_type === "CRYPTO OBSIDIAN" ? "crypto-obsidian-cancel-button" : ""}`}
              onClick={onClose}
            >
              Cancel
            </Button>
          </form>
        </div>
      )}
    </div>
  );
};

const stateToProps = (state) => ({
  themeMode: state.themeMode.themeMode,
  userSettings: state.userSettings.userSettings,
});

const dispatchToProps = (dispatch) => ({
  submitContactForm: (formData) => dispatch(contactFormLoad(formData)),
  setUserSettings: (settings, firstRender, fetch) =>
    dispatch(addUserSettingsLoad(settings, firstRender, fetch)),
});

export default connect(stateToProps, dispatchToProps)(ContactForm);
